// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import cloneDeep from "lodash.clonedeep";

import {
  ActionType,
  IAction,
  IActionCloudIoTrendData,
  IActionCloudTrendData,
  IActionCloudTrendDataRow,
  IActionCloudTrendInfo,
  IActionNewChargerStatusNotification,
  IActionNewChargerData,
  IActionTypeChargerStateChangeList,
} from "actions";
import { IO_TREND_LIMIT } from "shared/types/cloudtypes";
import { INITIAL_STATE, IStateTrending, ITrendData, ITrendDataRow } from "states/trendingstates";
import { isSet } from "shared/utils/utils";
import { IState } from "states/states";
import { Aggregation } from "graphql/api";

function addOrReplaceTrendRow(trendData: ITrendData, row: ITrendDataRow) {
  const idx = trendData.findIndex((r) => r.timestamp === row.timestamp && r.tlId === row.tlId);
  if (idx >= 0) {
    trendData[idx] = row;
  } else {
    trendData.push(row);
  }
}

export default function trendingReducer(
  state: IStateTrending,
  action: IAction<any>,
  rootState: IState,
): IStateTrending {
  // type convert action string back into ActionType
  switch (action.type) {
    case ActionType.ACT_DISCONNECT_SITE:
      state = cloneDeep(INITIAL_STATE);
      return state;

    case ActionType.ACT_GET_TRENDINFO_SUCC: {
      const payload = action.payload as IActionCloudTrendInfo;
      state.minTrendTimestamp = payload.minTrendTimestamp;
      state.maxTrendTimestamp = payload.maxTrendTimestamp;
      state.minIoTimestamp =
        isSet(payload.minInputTimestamp) && isSet(payload.minOutputTimestamp) ?
          Math.min(payload.minInputTimestamp, payload.minOutputTimestamp)
        : isSet(payload.minInputTimestamp) ? payload.minInputTimestamp
        : payload.minOutputTimestamp;
      state.maxIoTimestamp =
        isSet(payload.maxInputTimestamp) && isSet(payload.maxOutputTimestamp) ?
          Math.max(payload.maxInputTimestamp, payload.maxOutputTimestamp)
        : isSet(payload.maxInputTimestamp) ? payload.maxInputTimestamp
        : payload.maxOutputTimestamp;
      return state;
    }

    case ActionType.ACT_GET_CHARGERTREND_SUCC: {
      const payload = action.payload as IActionCloudTrendDataRow[];
      // clear potentially present charger data to keep memory footprint low
      state.chargerTrend = {};
      payload.forEach((curRow) => {
        if (isSet(curRow.chargerId)) {
          const connector = curRow.data.tlId;
          if (!isSet(connector)) {
            console.warn("cloud trend for foreign charger", curRow);
            return;
          }
          // ensure existing charger trend array
          if (!state.chargerTrend![curRow.chargerId]) {
            state.chargerTrend![curRow.chargerId] = [];
          }
          state.chargerTrend![curRow.chargerId].push(curRow.data);
        } else {
          console.warn("no charger id in cloud trend", curRow);
        }
      });
      return state;
    }

    case ActionType.ACT_GET_TREND_REQ:
      // before we load a new trend, we delete all existing data to
      // not mix data with different aggregations
      state.trendData = [];
      state.loadedTrendWindow = undefined;
      return state;

    case ActionType.ACT_GET_CLOUDTREND_SUCC: {
      const payload = action.payload as IActionCloudTrendData;
      state.loadedTrendWindow = payload.trendWindow;
      payload.data.forEach((curRow) => {
        if (!isSet(curRow.chargerId)) {
          // store data
          // we deleted all data at GET_TREND_REQ such that we can just add new
          // rows here without checking for existing data
          state.trendData.push(curRow.data);
        } else {
          console.warn("charger id in cloud trend", curRow);
        }
      });
      return state;
    }

    case ActionType.ACT_APPEND_CLOUDTREND: {
      const payload = action.payload as IActionCloudTrendDataRow;
      // we only append trends for un-aggregated views at end of scale; this also skips rows
      // during a reload, which delivers them anyway
      if (state.loadedTrendWindow?.isLive) {
        // we might got a trend of a non-subscribed tariff line - anyway, just store it
        // a reload running concurrently to our read may have delivered this row already
        addOrReplaceTrendRow(state.trendData, payload.data);
      }
      return state;
    }

    case ActionType.ACT_GET_TREND_FAIL:
      // delete trend data to avoid showing old series
      state.trendData = [];
      state.loadedTrendWindow = undefined;
      return state;

    case ActionType.ACT_CHANGE_TREND_SUCC: {
      const payload = action.payload as IActionCloudTrendDataRow;
      addOrReplaceTrendRow(state.trendData, payload.data);
      return state;
    }

    case ActionType.ACT_GET_CLOUDIOTREND_SUCC: {
      const payload = action.payload as IActionCloudIoTrendData;
      // before we store a new cloud trend, we delete all existing data to
      // not mix data with different aggregations
      state.ioTrendAggr = {};
      // we would not need to delete ioTrendFull as adding data there is always consistent/OK
      // but we can do it anyway to free up memory
      state.ioTrendFull = {};
      // clear TrendCount - otherwise we still see the month items in year-view
      state.ioTrendCount = {};
      // select trend storage to use
      const storage =
        payload.aggregation === Aggregation.NONE ? state.ioTrendFull : state.ioTrendAggr;
      const countStorage = state.ioTrendCount;

      payload.data.forEach((curRow) => {
        // ensure timestamp entry
        if (!Array.isArray(storage[curRow.timestamp])) {
          storage[curRow.timestamp] = [];
        }
        // ensure input/output structure
        const sourceIndex = curRow.source === "input" ? 0 : 1;
        if (!Array.isArray(storage[curRow.timestamp][sourceIndex])) {
          storage[curRow.timestamp][sourceIndex] = [];
        }
        storage[curRow.timestamp][sourceIndex][curRow.index] = curRow.value;

        if (!Array.isArray(countStorage[curRow.timestamp])) {
          countStorage[curRow.timestamp] = [];
        }
        if (payload.aggregation !== Aggregation.NONE && curRow.count != null) {
          if (!Array.isArray(countStorage[curRow.timestamp][sourceIndex])) {
            countStorage[curRow.timestamp][sourceIndex] = [];
          }
          countStorage[curRow.timestamp][sourceIndex][curRow.index] = curRow.count!;
        }
      });
      // set info if we are querying more data...
      state.queryMoreData = payload.queryMoreData;

      // check for limited response, in main io trend result first
      state.isIoTrendRestricted = payload.data.length >= IO_TREND_LIMIT;

      return state;
    }

    // if our main view shows aggregated data, we also get non-aggregated data for digital IOs
    case ActionType.ACT_GET_CLOUDIOTREND_FULL_SUCC: {
      const payload = action.payload as IActionCloudIoTrendData;
      payload.data.forEach((curRow) => {
        // ensure timestamp entry
        if (!Array.isArray(state.ioTrendFull[curRow.timestamp])) {
          state.ioTrendFull[curRow.timestamp] = [];
        }
        // ensure input/output structure
        const sourceIndex = curRow.source === "input" ? 0 : 1;
        if (!Array.isArray(state.ioTrendFull[curRow.timestamp][sourceIndex])) {
          state.ioTrendFull[curRow.timestamp][sourceIndex] = [];
        }
        state.ioTrendFull[curRow.timestamp][sourceIndex][curRow.index] = curRow.value;
      });
      // check for limited response, also in second full data, which is necessarily bigger
      state.isIoTrendRestricted = payload.data.length >= IO_TREND_LIMIT;
      state.queryMoreData = payload.queryMoreData;
      return state;
    }

    case ActionType.ACT_GET_CLOUDIOTREND_FULL_FAIL:
      // keep the aggregated data, but stop waiting for the raw data
      state.queryMoreData = false;
      return state;

    case ActionType.ACT_GET_IOTREND_FAIL:
      // delete trend data to avoid showing old series
      state.ioTrendFull = {};
      state.ioTrendAggr = {};
      state.ioTrendCount = {};
      return state;

    case ActionType.ACT_GET_CHARGING_STATES_SUCC: {
      const payload = action.payload as IActionTypeChargerStateChangeList;
      state.chargerStateData = payload;
      return state;
    }

    case ActionType.ACT_NEW_OCPP_DATA: {
      const payload = action.payload as IActionNewChargerData;
      const selectedChargingSession = rootState.ocpp.pendingChargingSession.find(
        (curItem) => curItem.id === rootState.router.transaction,
      );
      // check whether we are currently viewing this session
      if (
        isSet(selectedChargingSession) &&
        selectedChargingSession.fk_charger === payload.chargerId &&
        selectedChargingSession.connector === payload.connector
      ) {
        // ensure existing data structure
        if (!state.chargerTrend) {
          state.chargerTrend = {};
        }
        if (!state.chargerTrend[payload.chargerId]) {
          state.chargerTrend[payload.chargerId] = [];
        }
        // did we get new metering values?
        if (isSet(payload.power)) {
          // identify last data entry
          // do NOT use very latest but use the latest before the current entry
          const lastTimestamp = state.chargerTrend[payload.chargerId]
            .filter((r) => r.tlId === payload.connector && r.timestamp < payload.timestamp)
            .map((r) => r.timestamp)
            .sort((a, b) => a - b)
            .pop();
          if (!lastTimestamp) {
            console.warn("first power timestamp?", payload, state.chargerTrend[payload.chargerId]);
          }
          // take 30 s as default for very first entry, which should not happen anyway
          const regperiod = lastTimestamp ? payload.timestamp - lastTimestamp : 30;
          addOrReplaceTrendRow(state.chargerTrend[payload.chargerId], {
            timestamp: payload.timestamp,
            tlId: payload.connector,
            regperiod,
            energy: (payload.power * regperiod) / 3600,
            power: payload.power,
            soc: payload.soc,
          });
        }
      }
      return state;
    }

    case ActionType.ACT_NEW_CHARGER_STATUS: {
      const payload = action.payload as IActionNewChargerStatusNotification;
      const selectedChargingSession = rootState.ocpp.pendingChargingSession.find(
        (curItem) => curItem.id === rootState.router.transaction,
      );
      // check whether we are currently viewing this session
      if (
        isSet(selectedChargingSession) &&
        selectedChargingSession.fk_charger === payload.chargerId &&
        (selectedChargingSession.connector === payload.connector || payload.connector === 0)
      ) {
        // did we get a new connector state?
        // we just collect state changes from connector, but not from station (connector 0) here...
        if (isSet(payload.statusCode)) {
          if (state.chargerStateData === undefined) state.chargerStateData = [];
          state.chargerStateData.push({
            id: -1, // DB table id not provided and not needed
            fk_charger: payload.chargerId,
            connector: payload.connector,
            timestamp: payload.timestamp,
            source: payload.source,
            statusCode: payload.statusCode,
            errorCode: payload.errorCode,
            info: payload.info,
          });
        }
      }
      return state;
    }

    default:
      return state;
  }
}
